import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../finance/auth/AuthContext';
import { getMyMeetings, getMeetingsByRange, deleteMeeting } from '../../api/meetings';
import { toast } from 'react-hot-toast';
import { FiCalendar, FiPlus, FiClock, FiMapPin, FiUser, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import MeetingFormModal from './MeetingFormModal';
import CalendarMonthView from './CalendarMonthView';
import CalendarWeekView from './CalendarWeekView';
import CalendarDayView from './CalendarDayView';
import MeetingTooltip from './MeetingTooltip';
import ConfirmModal from '../shared/ConfirmModal';
import { formatTime } from './utils';

const CalendarPage = () => {
  const navigate = useNavigate();
  const { role, depts, isSuperadmin } = useAuth();
  
  const isAdmin = String(role).toUpperCase() === 'ADMIN' || isSuperadmin;
  const hasSalesDept = depts?.some((d) => String(d).toUpperCase() === 'SALES') || isAdmin;
  const canCreateEdit = hasSalesDept;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day', 'agenda'
  const [agendaFilter, setAgendaFilter] = useState('all');
  
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [meetingToEdit, setMeetingToEdit] = useState(null);
  const [initialModalDate, setInitialModalDate] = useState(null);
  const [clickedEvent, setClickedEvent] = useState(null);

  useEffect(() => {
    fetchMeetings();
  }, [currentDate, viewMode, agendaFilter]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      if (viewMode === 'agenda') {
        const data = await getMyMeetings(agendaFilter, 0, 50);
        setMeetings(data.content || []);
      } else {
        const start = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1).toISOString();
        const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0).toISOString();
        const data = await getMeetingsByRange(start, end);
        setMeetings(data);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (meeting, e) => {
    e.stopPropagation();
    setMeetingToEdit(meeting);
    setIsModalOpen(true);
  };

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, meeting: null, loading: false });

  const handleDelete = (meetingItem, e) => {
    if (e) e.stopPropagation();
    setDeleteConfirm({ open: true, meeting: meetingItem, loading: false });
  };

  const confirmDeleteMeeting = async () => {
    const meeting = deleteConfirm.meeting;
    if (!meeting) return;
    setDeleteConfirm(prev => ({ ...prev, loading: true }));
    try {
      await deleteMeeting(meeting.id);
      toast.success(`Meeting "${meeting.title || 'Event'}" has been deleted.`);
      setDeleteConfirm({ open: false, meeting: null, loading: false });
      fetchMeetings();
    } catch (error) {
      toast.error('Failed to delete meeting');
      setDeleteConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCreate = () => {
    setMeetingToEdit(null);
    setInitialModalDate(null);
    setIsModalOpen(true);
  };

  const handleDateClick = (date) => {
    setMeetingToEdit(null);
    setInitialModalDate(date);
    setIsModalOpen(true);
  };

  const handleEventClick = (meeting, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setClickedEvent({ meeting, x: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom });
  };

  const navigateToDetails = (id) => {
    setClickedEvent(null);
    navigate(`/calendar/meeting/${id}`);
  };

  const changeDate = (offset) => {
    const d = new Date(currentDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() + offset);
    else if (viewMode === 'week') d.setDate(d.getDate() + (offset * 7));
    else if (viewMode === 'day') d.setDate(d.getDate() + offset);
    setCurrentDate(d);
  };

  const formatDateLabel = () => {
    if (viewMode === 'month') return currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    if (viewMode === 'day') return currentDate.toLocaleString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    if (viewMode === 'week') {
      const d = new Date(currentDate);
      const day = d.getDay();
      const start = new Date(d.setDate(d.getDate() - day));
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      if (start.getMonth() === end.getMonth()) {
        return `${start.toLocaleString('default', { month: 'long' })} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
      }
      return `${start.toLocaleString('default', { month: 'short' })} ${start.getDate()} - ${end.toLocaleString('default', { month: 'short' })} ${end.getDate()}, ${start.getFullYear()}`;
    }
    return 'Agenda';
  };

  return (
    <>
      <div className="p-0 sm:p-4 max-w-7xl mx-auto space-y-4">
        
        {/* Header / Toolbar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg">
              <FiCalendar className="w-5 h-5" />
            </div>
            {viewMode !== 'agenda' && (
              <div className="flex items-center gap-2">
                <button onClick={() => changeDate(-1)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-600 transition-colors"><FiChevronLeft size={20}/></button>
                <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1.5 text-sm font-medium hover:bg-slate-100 rounded-md text-slate-700 transition-colors">Today</button>
                <button onClick={() => changeDate(1)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-600 transition-colors"><FiChevronRight size={20}/></button>
              </div>
            )}
            <h2 className="text-base sm:text-xl font-bold text-slate-800">
              {formatDateLabel()}
            </h2>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
            {/* View Switcher */}
            <div className="flex bg-slate-100 p-1 rounded-lg shrink-0">
              {['day', 'week', 'month', 'agenda'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all capitalize
                    ${viewMode === mode ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}
                  `}
                >
                  {mode}
                </button>
              ))}
            </div>

            {canCreateEdit && (
              <button 
                onClick={handleCreate}
                className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 font-medium shadow-sm"
              >
                <FiPlus /> <span className="hidden sm:inline">New Event</span>
              </button>
            )}
          </div>
        </div>

        {/* Filters for Agenda */}
        {viewMode === 'agenda' && (
          <div className="flex gap-2 pb-2 overflow-x-auto">
            {['all', 'today', 'upcoming', 'completed', 'organizer'].map(f => (
              <button
                key={f}
                onClick={() => setAgendaFilter(f)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors capitalize whitespace-nowrap border
                  ${agendaFilter === f ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}
                `}
              >
                {f}
              </button>
            ))}
          </div>
        )}

        {/* Content View */}
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center rounded-xl">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {viewMode === 'month' && <CalendarMonthView currentDate={currentDate} meetings={meetings} onDateClick={handleDateClick} onEventClick={handleEventClick} />}
          {viewMode === 'week' && <CalendarWeekView currentDate={currentDate} meetings={meetings} onDateClick={handleDateClick} onEventClick={handleEventClick} />}
          {viewMode === 'day' && <CalendarDayView currentDate={currentDate} meetings={meetings} onDateClick={handleDateClick} onEventClick={handleEventClick} />}
          
          {clickedEvent && <MeetingTooltip {...clickedEvent} onClose={() => setClickedEvent(null)} onNavigate={navigateToDetails} />}

          {viewMode === 'agenda' && (
            <div className="grid gap-6">
              {meetings.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <p className="text-slate-500 font-medium">No meetings found.</p>
                </div>
              ) : (
                meetings.map(meeting => (
                  <div 
                    key={meeting.id} 
                    onClick={() => handleEventClick(meeting.id)}
                    className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer flex flex-col sm:flex-row gap-4 justify-between group"
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 text-xs font-bold rounded uppercase
                          ${meeting.status === 'SCHEDULED' ? 'bg-blue-50 text-blue-600' : 
                            meeting.status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-600'}
                        `}>
                          {meeting.status}
                        </span>
                        <h3 className="text-lg font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                          {meeting.title}
                        </h3>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5 font-medium text-slate-700">
                          <FiClock className="text-blue-500" /> 
                          {new Date(meeting.meetingStart).toLocaleDateString()} • {formatTime(meeting.meetingStart)} - {formatTime(meeting.meetingEnd)}
                        </div>
                        {meeting.location && <div className="flex items-center gap-1.5 text-slate-600"><FiMapPin className="text-slate-400" /> {meeting.location}</div>}
                        {meeting.organizerName && <div className="flex items-center gap-1.5 text-slate-600"><FiUser className="text-slate-400" /> {meeting.organizerName}</div>}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <MeetingFormModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          meeting={meetingToEdit}
          initialDate={initialModalDate}
          onSuccess={() => {
            setIsModalOpen(false);
            fetchMeetings();
          }}
        />
      )}

      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, meeting: null, loading: false })}
        onConfirm={confirmDeleteMeeting}
        loading={deleteConfirm.loading}
        title="Delete Meeting?"
        message={`Are you sure you want to delete "${deleteConfirm.meeting?.title || 'this meeting'}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};

export default CalendarPage;
